import { useCallback, useEffect, useMemo, useState } from "react";
import {
  CONTRACT_STATUS_OVERRIDES,
  ContractStatus,
  IContractStatusEntry,
  WorkspaceCnpjIndex,
  getDefaultContractStatus,
  onlyDigits,
  resolveContractStatusForCnpj,
  resolveEntryFederalTaxId,
} from "./contractStatus";

const STORAGE_KEY = "@conducco:operationalPanel:contractStatusOverrides";

/**
 * Índice `workspaceId` -> CNPJ, persistido à parte das marcações. Existe por um
 * motivo só: a tela de Cadastro (fluxo essencial da plataforma) NÃO foi
 * alterada para mandar o CNPJ na marcação, então quem liga marcação a CNPJ é
 * esta camada. Serve tanto para a marcação nova (que nasce sem CNPJ) quanto
 * para a antiga, gravada antes do campo existir.
 */
const WORKSPACE_CNPJ_INDEX_KEY = "@conducco:operationalPanel:workspaceCnpjIndex";

function readSessionOverrides(): IContractStatusEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as IContractStatusEntry[]) : [];
  } catch {
    return [];
  }
}

function writeSessionOverrides(entries: IContractStatusEntry[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
}

function readWorkspaceCnpjIndex(): WorkspaceCnpjIndex {
  try {
    const raw = localStorage.getItem(WORKSPACE_CNPJ_INDEX_KEY);
    return raw ? (JSON.parse(raw) as WorkspaceCnpjIndex) : {};
  } catch {
    return {};
  }
}

/**
 * Pares vistos nesta sessão, em memória. `getContractStatus` roda DURANTE o
 * render (é chamado no corpo do componente da tela de Cadastro), então ali só
 * se pode escrever em memória — a gravação em `localStorage` e o `setState`
 * acontecem no `useEffect` de flush, depois do commit.
 */
const paresVistos = new Map<string, string>();

/** CNPJ vazio NUNCA entra no índice: seria uma atribuição falsa, e atribuição falsa esconde CNPJ errado. */
function rememberWorkspaceCnpj(workspaceId: string, buyerFederalTaxId: string): void {
  const digitos = onlyDigits(buyerFederalTaxId ?? "");
  if (!workspaceId || !digitos) return;
  paresVistos.set(workspaceId, digitos);
}

/**
 * Último índice já fundido nesta sessão. Existe para que o flush seja
 * idempotente MESMO quando a gravação falha (cota cheia, modo privado): sem
 * isto, o `useEffect` sem deps veria "mudou" a cada render e entraria em loop
 * de `setState`.
 */
let indiceFundido: WorkspaceCnpjIndex | null = null;

/** Funde os pares vistos no índice persistido. Devolve o índice novo só quando algo mudou. */
function flushWorkspaceCnpjIndex(): WorkspaceCnpjIndex | null {
  if (paresVistos.size === 0) return null;
  const proximo: Record<string, string> = { ...readWorkspaceCnpjIndex(), ...indiceFundido };
  let mudou = false;
  paresVistos.forEach((federalTaxId, workspaceId) => {
    if (proximo[workspaceId] !== federalTaxId) {
      proximo[workspaceId] = federalTaxId;
      mudou = true;
    }
  });
  if (!mudou) return null;
  indiceFundido = proximo;
  try {
    localStorage.setItem(WORKSPACE_CNPJ_INDEX_KEY, JSON.stringify(proximo));
  } catch {
    /* cota cheia / modo privado: o índice degrada para memória desta sessão. */
  }
  return proximo;
}

/**
 * Segunda fonte do índice, além da tela de Cadastro: o payload de
 * `GET /v2/operational-panel/cnpj/:federalTaxId`, que lista os workspaces do
 * CNPJ. Com isso o operador que abre um CNPJ no painel já resolve as marcações
 * antigas daquele CNPJ, sem precisar voltar ao Cadastro workspace por
 * workspace. Chamar de dentro de `useEffect` — grava em `localStorage`.
 */
export function learnWorkspaceCnpjPairs(federalTaxId: string, workspaceIds: string[]): void {
  workspaceIds.forEach((workspaceId) => rememberWorkspaceCnpj(workspaceId, federalTaxId));
  flushWorkspaceCnpjIndex();
}

export function useContractStatus() {
  const [sessionOverrides, setSessionOverrides] = useState<IContractStatusEntry[]>(readSessionOverrides);
  const [workspaceCnpjIndex, setWorkspaceCnpjIndex] = useState<WorkspaceCnpjIndex>(readWorkspaceCnpjIndex);

  /** Sem array de deps: o que dispara gravação é o flush ter algo novo, não uma dependência específica. */
  useEffect(() => {
    const proximo = flushWorkspaceCnpjIndex();
    if (proximo) setWorkspaceCnpjIndex(proximo);
  });

  // Prioridade: sessão atual > arquivo versionado > regra de partida por seller/CNPJ.
  const overridesByWorkspaceId = useMemo(() => {
    const map = new Map<string, IContractStatusEntry>();
    [...CONTRACT_STATUS_OVERRIDES, ...sessionOverrides].forEach((entry) => map.set(entry.workspaceId, entry));
    return map;
  }, [sessionOverrides]);

  /**
   * Grão WORKSPACE — o da tela de Cadastro. Não precisa de índice: a marcação
   * já é chaveada por `workspaceId`, então a marcação antiga sempre funcionou
   * aqui. O CNPJ recebido serve à regra de partida e, de carona, alimenta o
   * índice que a tela inicial usa para resolver marcação sem CNPJ.
   */
  const getContractStatus = useCallback(
    (workspaceId: string, sellerName: string, buyerFederalTaxId: string): ContractStatus => {
      rememberWorkspaceCnpj(workspaceId, buyerFederalTaxId);
      return overridesByWorkspaceId.get(workspaceId)?.status ?? getDefaultContractStatus(sellerName, buyerFederalTaxId);
    },
    [overridesByWorkspaceId]
  );

  /**
   * Status no grão CNPJ — o grão da tela inicial do painel. A regra de
   * resolução (marcação explícita vence a regra de partida; marcação sem CNPJ
   * vale pelo `workspaceId` via índice; entre marcações do mesmo CNPJ o mais
   * visível ganha) está documentada em `resolveContractStatusForCnpj`. Aqui só
   * entra a lista já deduplicada por workspace, pra uma marcação de sessão não
   * somar com a versionada do mesmo workspace.
   */
  const getCnpjContractStatus = useCallback(
    (cnpj: { sellerName: string; federalTaxId: string }): ContractStatus =>
      resolveContractStatusForCnpj(cnpj, [...overridesByWorkspaceId.values()], workspaceCnpjIndex),
    [overridesByWorkspaceId, workspaceCnpjIndex]
  );

  /**
   * `buyerFederalTaxId` é OPCIONAL de propósito: a tela de Cadastro chama com
   * três argumentos e não vai ser alterada (fluxo essencial da plataforma). Sem
   * ele, o CNPJ sai do par que a leitura desta mesma tela já registrou para
   * este `workspaceId` — e, na falta dele, do índice persistido. Se nenhum dos
   * dois souber, a marcação é gravada sem CNPJ e continua resolvível pelo
   * `workspaceId` assim que o índice aprender o par.
   */
  const setContractStatus = useCallback(
    (workspaceId: string, status: ContractStatus, author: string, buyerFederalTaxId?: string) => {
      const informado = onlyDigits(buyerFederalTaxId ?? "");
      const conhecido = informado || paresVistos.get(workspaceId) || onlyDigits(workspaceCnpjIndex[workspaceId] ?? "");

      const entry: IContractStatusEntry = {
        workspaceId,
        status,
        author,
        updatedAt: new Date().toISOString(),
        ...(conhecido ? { buyerFederalTaxId: conhecido } : {}),
      };

      setSessionOverrides((current) => {
        const next = [...current.filter((item) => item.workspaceId !== workspaceId), entry];
        writeSessionOverrides(next);
        return next;
      });
    },
    [workspaceCnpjIndex]
  );

  /**
   * Mesmo padrão de "copiar marcações como código" do Silenciar — trecho pronto
   * pra colar em contractStatus.ts. Emite o CNPJ RESOLVIDO (o da marcação ou o
   * do índice): o arquivo versionado não tem índice em runtime, então o que não
   * for resolvido aqui não seria resolvível lá.
   */
  const generateCodeSnippet = useCallback(() => {
    return sessionOverrides
      .map((entry) => {
        const federalTaxId = resolveEntryFederalTaxId(entry, workspaceCnpjIndex);
        return `  { workspaceId: "${entry.workspaceId}", status: "${entry.status}", author: "${entry.author}", updatedAt: "${entry.updatedAt}", buyerFederalTaxId: "${federalTaxId}" },`;
      })
      .join("\n");
  }, [sessionOverrides, workspaceCnpjIndex]);

  return {
    sessionOverrides,
    getContractStatus,
    getCnpjContractStatus,
    setContractStatus,
    generateCodeSnippet,
  };
}
